import React, { FC, useEffect, useState } from 'react';
import {
  useBridgeApi,
  useButton,
  useCurrency,
  useDeviceInfo,
  useI18n,
  useLogger,
  useSelector,
} from '~Hooks';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import useModal from '~ComponentsImproved/Modal/useModal';
import DemoNoPaymentsAllowed from '~Components/DemoHandler/NoPaymentsAllowed';
import PaymentStatus from '~Components/PaymentStatus';
import { useStorage } from '~Components/Storage';
import { sortByCourseTypeOrder } from '~Utils/sortArticles';
import { useApolloClient } from '@apollo/client';
import { combineArticles } from '~Utils/combineArticles';
import Heading from '~ComponentsImproved/Typography/Heading';
import PriceSection from '../../../Handy/Scenes/ChequeDetails/PriceSection';
import { SERVICE_TYPE } from '../../../../../__queries__/globalTypes';
import Portal from '~ComponentsImproved/Portal';
import { BlueButton, Box } from '~Styles';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import { CashDrawerOpenInfo } from '~Scenes/POS/Components/Cart';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import PayWith from '~Scenes/Tables/Components/PayWith';
import styled from '@emotion/styled';
import Modal from '~ComponentsImproved/Modal';
import { ModalWrapper } from '~Components/ClaimDevice/styles';
import Icon from '~ComponentsImproved/Icon';
import SyncProblemIcon from '~Assets/icons/sync_problem.svg';
import { DateTime } from 'luxon';
import PayButton from '~Components/PayButton';
import BeeperSelect from '~Components/BeeperSelect';
import { useBookings } from '~Providers/BookingsProvider';

const HeaderBox = styled(Box)<{ minHeight?: string }>`
  text-align: center;
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const PaymentWrapper = styled.div<{ isPhone?: boolean }>`
  position: fixed;
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  padding: ${({ isPhone }) => (isPhone ? '64px 16px 32px' : '24px 16px 24px')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--colorBlueGray1300);
  color: #fff;
  display: flex;
  flex-flow: column nowrap;
  z-index: 3;
`;

const ItemWrapper = styled.li`
  list-style: none;
  display: flex;
  position: relative;
  padding: 10px 0 10px;
  align-items: center;

  p {
    padding-right: 12px;
    margin: 0;
    word-break: break-word;
  }
`;

const ItemPrice = styled.span`
  padding-left: 12px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InitialPrice = styled.span`
  color: var(--colorBlueGray600);
  text-decoration: line-through;
  font-size: 12px;
`;

const Qty = styled.span`
  width: 32px;
  margin-right: 8px;
  display: inline-block;
  min-width: 32px;
`;

const ItemsList = styled.div`
  max-height: calc(100vh - 200px);
  overflow: hidden;
  position: relative;
  margin-bottom: auto;

  &:before {
    content: '';
    height: 40px;
    width: 100%;
    position: absolute;
    top: auto;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0), var(--colorBlueGray1300));
    z-index: 1;
  }
`;

const ItemsListInner = styled.ul`
  overflow-y: auto;
  height: 100%;
  box-sizing: border-box;
  padding: 0 0 24px;
  margin: 0;
`;

type PaymentInfo = {
  amount: number;
  id: string;
  variant?: string;
};

interface Props {
  easyId?: string;
  ongoingOrder?: boolean;
  serviceType?: SERVICE_TYPE;
  cancelOrder?: () => void;
  selectedTable: {
    id: string;
    code?: string;
  } | null;
  selectedChequeId?: string;
  isQSR?: boolean;
  /** When true, automatically initiates payment with the default method as soon as the cheque has items */
  autoInitiatePayment?: boolean;
  /** Called when auto-payment has been initiated, so the parent can reset the flag */
  onAutoPaymentInitiated?: () => void;
}

const PayButtons: FC<Props> = ({
  easyId,
  ongoingOrder,
  serviceType = SERVICE_TYPE.EAT_HERE,
  cancelOrder,
  selectedTable,
  selectedChequeId,
  isQSR, // used only for QSR in handy
  autoInitiatePayment,
  onAutoPaymentInitiated,
}) => {
  const logger = useLogger('handy');
  const deviceInfo = useDeviceInfo();
  const { i18n } = useI18n();
  const demoMode = useSelector((state) => state.demoMode);
  const demoPopup = useModal();
  const syncModal = useModal();
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const hasAdyen = paymentMethods.some((method) => method.id === 'ADYEN');
  const tableAPI = useTableApi();
  const universe = useTableUniverse();
  const currency = useCurrency();
  const staff = useSelector((state) => state.staff);
  const [storage] = useStorage();
  const client = useApolloClient();
  const cashDrawer = useCashDrawer();
  const bridgeApi = useBridgeApi();
  const bookings = useBookings();
  const lastTableEventData = useSelector((state) => state.lastTableEventData);
  const cheques = universe.cheques.filter((c) => c.tableId === selectedTable?.id);
  const cheque = cheques.find((c) => c.id === selectedChequeId);

  const chequeItems = universe.items.filter((c) => c.chequeId === selectedChequeId);

  const sortedItems = chequeItems?.length > 0 ? sortByCourseTypeOrder(chequeItems, client) : [];

  const itemsArr = storage.compressedCart ? combineArticles(sortedItems) : sortedItems;

  const order = useSelector(
    (state) => state.POSOrders.filter((o) => o.chequeId === selectedChequeId)[0] || null,
  );

  const payment = useSelector(
    (state) => state.POSPayments.filter((p) => order && p.order === order.id)[0] || null,
  );

  const beeperList = useSelector((state) => state.beeperList);
  const [showBeeperSelector, setShowBeeperSelector] = useState<boolean>(false);
  const [beeperEasyId, setBeeperEasyId] = useState<string | null>(null);
  const [pendingPayment, setPendingPayment] = useState<{
    id: string;
    customAmount?: number;
    variant?: string;
  } | null>(null);

  const amountLeftToPay = cheque ? cheque.amount - cheque.discount - cheque.paidAmount : 0;

  const paymentPending = payment?.status === 'PENDING';

  // When a beeper is selected, proceed with the pending payment
  useEffect(() => {
    if (pendingPayment && beeperEasyId) {
      handleSetupPayment(pendingPayment.id, pendingPayment.customAmount, pendingPayment.variant);
      setPendingPayment(null);
    }
  }, [beeperEasyId]);

  const handlePayWithBeeperCheck = (id: string, customAmount?: number, variant?: string) => {
    const currentEasyId = beeperEasyId || easyId;
    const hasBeeper = currentEasyId && currentEasyId.includes('•');

    if (storage.requireBeeperNumber && beeperList.length > 0 && !hasBeeper) {
      setPendingPayment({ id, customAmount, variant });
      setShowBeeperSelector(true);
      return;
    }

    handleSetupPayment(id, customAmount, variant);
  };

  const handleSetupPayment = (id: string, customAmount?: number, variant?: string) => {
    if (chequeItems.length === 0 && !selectedChequeId) {
      return;
    }

    if (demoMode) {
      demoPopup.toggle();
      return;
    }

    // Check if TableSnapshot timestamp is more than 30 seconds old (indicates that cheque might be out of sync)
    if (lastTableEventData && !isQSR) {
      const snapshotTime = DateTime.fromISO(lastTableEventData).toMillis();
      const currentTime = DateTime.now().toMillis();
      const timeDifference = (currentTime - snapshotTime) / 1000;

      if (timeDifference > 30) {
        logger.warn(
          `Showing resync modal, diff: ${timeDifference} seconds, cheque.id: ${cheque?.id}, cheque.amount: ${cheque?.amount}, itemsLength: ${chequeItems.length}`,
        );
        syncModal.toggle();
        return;
      }
    }

    initiatePayment({ id, amount: customAmount || amountLeftToPay, variant });
  };

  // QSR-specific: If the payment is cancelled, we cancel the ordering process and go back to the cheque view or partial payment view
  useEffect(() => {
    if (ongoingOrder && payment?.status === 'FAILED') {
      cancelOrder && cancelOrder();
    }
  }, [payment, ongoingOrder]);

  const initiatePayment = (paymentInfo: PaymentInfo) => {
    if (demoMode) {
      return;
    }

    if (!selectedChequeId) {
      return;
    }

    // Sometimes the amount can contain floating point errors, so we round it
    // to two decimal points. This will never cause a rounding error as we only
    // deal with whole cents.
    // Example: 100.23000000000001 => 100.23
    const roundedAmount = Math.round(paymentInfo.amount * 100) / 100;

    const resolvedEasyId = beeperEasyId || easyId;
    const orderId = resolvedEasyId ? resolvedEasyId : selectedTable?.code ?? '---';

    tableAPI.initiatePayment(
      selectedChequeId,
      orderId,
      paymentInfo.id,
      roundedAmount,
      paymentInfo.variant,
      staff ? staff.id : undefined,
      undefined,
      serviceType,
      cheque?.bookingData?.externalId,
    );

    logger.info('Triggered payment', {
      cheque,
      table: selectedTable,
      amount: paymentInfo.amount,
      roundedAmount,
      currency,
      paymentMethod: paymentInfo.id,
      amountLeftToPay,
      isPartial: paymentInfo.amount !== amountLeftToPay,
      bookingId: cheque?.bookingData?.externalId,
    });
  };

  const hasPartialPayment = cheque && cheque.paidAmount > 0 && !paymentPending;

  const resyncButton = useButton({
    onClick: () => {
      bridgeApi.reload();
    },
  });

  // ongoingOrder && !hasPartialPayment is a special case where the first payment is made this avoids flickering screens
  if (isQSR && (paymentPending || (ongoingOrder && !hasPartialPayment))) {
    return (
      <Portal>
        <PaymentWrapper>
          <Box padding="large" marginTop="large">
            {payment && <PaymentStatus status={payment.status} paymentId={payment.id} />}
          </Box>
        </PaymentWrapper>
      </Portal>
    );
  }

  return (
    <>
      {/* This is only shown when we are in QSR mode and there is a partial payment. The POS is blocked until the cheque is paid. */}
      {isQSR && hasPartialPayment ? (
        <Portal>
          <PaymentWrapper isPhone={deviceInfo?.isPhone}>
            <Heading level={2} color="var(--colorWhite)" bottom={16}>
              {i18n('Handy.Payment.ReadyForNext')}
            </Heading>
            <ItemsList>
              <ItemsListInner>
                {itemsArr.map((item) => {
                  const itemCount = ('count' in item ? item.count : item.fraction) ?? 1;

                  return (
                    <ItemWrapper key={item.id}>
                      <Qty>{Math.round(itemCount * 100) / 100}</Qty>
                      <p>
                        {item.title}
                        {item.message && (
                          <i>
                            <br />
                            {item.message}
                          </i>
                        )}
                        {item.richVariations
                          ?.filter((rv) => rv.price > 0)
                          .map((variation) => (
                            <i>
                              <br />+ {variation.name}
                            </i>
                          ))}
                      </p>
                      <ItemPrice>
                        {item.discount > 0 && (
                          <InitialPrice>{currency.format(item.amount)}</InitialPrice>
                        )}
                        {currency.format(item.amount - item.discount)}
                      </ItemPrice>
                    </ItemWrapper>
                  );
                })}
              </ItemsListInner>
            </ItemsList>
            <PriceSection />

            {cashDrawer.isOpen ? (
              <CashDrawerOpenInfo>
                <Paragraph level={3} color="var(--colorBlyeGray1200)">
                  {i18n('Cashdrawer.BlockedPayments')}
                </Paragraph>
              </CashDrawerOpenInfo>
            ) : (
              <ButtonRow>
                <PayButton
                  onPay={(paymentMethodId, poiid) =>
                    handleSetupPayment(paymentMethodId, undefined, poiid)
                  }
                />

                <PayWith
                  selectedChequeId={selectedChequeId}
                  paidAmount={cheque?.paidAmount}
                  onSelect={(methodId: string, customAmount?: number, variant?: string) =>
                    handleSetupPayment(methodId, customAmount, variant)
                  }
                  isHandyQSR={isQSR}
                />
              </ButtonRow>
            )}
          </PaymentWrapper>
        </Portal>
      ) : (
        <>
          {cashDrawer.isOpen ? (
            <CashDrawerOpenInfo>
              <Paragraph level={3} color="var(--colorBlyeGray1200)">
                {i18n('Cashdrawer.BlockedPayments')}
              </Paragraph>
            </CashDrawerOpenInfo>
          ) : (
            <ButtonRow>
              <PayButton
                onPay={(paymentMethodId, poiid) => {
                  if (isQSR) {
                    handlePayWithBeeperCheck(paymentMethodId, undefined, poiid);
                  } else {
                    handleSetupPayment(paymentMethodId, undefined, poiid);
                  }
                }}
                autoInitiatePayment={autoInitiatePayment && amountLeftToPay > 0}
                onAutoPaymentInitiated={onAutoPaymentInitiated}
              />

              <PayWith
                selectedChequeId={selectedChequeId}
                paidAmount={cheque?.paidAmount}
                onSelect={(methodId: string, customAmount?: number, variant?: string) => {
                  if (isQSR) {
                    handlePayWithBeeperCheck(methodId, customAmount, variant);
                  } else {
                    handleSetupPayment(methodId, customAmount, variant);
                  }
                }}
                isHandyQSR={isQSR}
              />
            </ButtonRow>
          )}
        </>
      )}

      {showBeeperSelector && (
        <BeeperSelect
          isHandheld={true}
          beeperList={beeperList}
          onSelect={(beeperId) => {
            setBeeperEasyId(beeperId);
            setShowBeeperSelector(false);
          }}
          onCancel={() => {
            setShowBeeperSelector(false);
            setPendingPayment(null);
          }}
        />
      )}

      <DemoNoPaymentsAllowed isShowing={demoPopup.isShowing} toggle={demoPopup.toggle} />

      <Modal isShowing={syncModal.isShowing} backdropClose={false}>
        <ModalWrapper
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="xs"
          justifyContent="center"
          padding="small">
          <HeaderBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            marginTop="xs"
            gap="xs"
            justifyContent="center">
            <Icon
              height="48px"
              width="48px"
              src={SyncProblemIcon}
              color="var(--colorBlueGray200)"
            />
            <Heading level={2} color="var(--colorWhite)">
              {i18n('Handy.Payment.OutOfSync')}
            </Heading>
            <Paragraph level={4} color="var(--colorBlueGray200)">
              {i18n('Handy.Payment.OutOfSync.Desc')}
            </Paragraph>
          </HeaderBox>

          <Box fluid display="flex" gap="large" marginTop="medium" marginBottom="xs">
            <BlueButton fluid {...resyncButton}>
              {i18n('Handy.Payment.OutOfSync.Resync')}
            </BlueButton>
          </Box>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default PayButtons;
